import { useState, useEffect, useRef, useCallback } from "react";
import api from "../utils/api";
import socket from "../socket";
import { toast } from "react-toastify";

export const useTableBooking = (restaurantId) => {
  const [tables, setTables] = useState([]);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const fetchTimeoutRef = useRef(null);
  const isFetchingRef = useRef(false);
  const restaurantIdRef = useRef(restaurantId);

  // Update ref when restaurantId changes
  useEffect(() => {
    restaurantIdRef.current = restaurantId;
  }, [restaurantId]);

  const fetchTables = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current || !restaurantIdRef.current) return;

    try {
      isFetchingRef.current = true;
      const res = await api.get(
        `/table/tables?restaurantId=${restaurantIdRef.current}`,
      );
      setTables(res.data);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error("Error loading tables", err);
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  // Debounced fetch to prevent rapid consecutive calls
  const debouncedFetchTables = useCallback(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    fetchTimeoutRef.current = setTimeout(() => {
      fetchTables();
    }, 300); // 300ms debounce
  }, [fetchTables]);

  useEffect(() => {
    if (!restaurantId) return;

    // Only join restaurant room once
    socket.emit("joinRestaurant", { restaurantId });

    // Register socket listeners
    socket.on("tables-updated", debouncedFetchTables);
    socket.on("order-updated", debouncedFetchTables);
    socket.on("bill-settled", debouncedFetchTables);
    socket.on("allocation-updated", debouncedFetchTables);

    // Initial fetch
    fetchTables();

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      socket.off("tables-updated", debouncedFetchTables);
      socket.off("order-updated", debouncedFetchTables);
      socket.off("bill-settled", debouncedFetchTables);
      socket.off("allocation-updated", debouncedFetchTables);
    };
  }, [restaurantId, debouncedFetchTables, fetchTables]);

  const handleToggleTable = async (id) => {
    try {
      await api.put(`/table/toggle/${id}`, { restaurantId });
      toast.success("Table configuration updated");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update");
    }
  };

  const createOrUpdateTable = async ({ _id, tableNumber, capacity }) => {
    try {
      if (_id) {
        await api.put(`/table/update/${_id}`, {
          tableNumber,
          capacity,
          restaurantId,
        });
        toast.success("Table updated successfully");
      } else {
        await api.post("/table/create", {
          tableNumber,
          capacity,
          restaurantId,
          generateQr: true,
        });
        toast.success("Table created successfully");
      }

      await fetchTables();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save table");
      return false;
    }
  };

  const handleBulkPrintDirect = () => {
    const printContainer = document.createElement("div");
    printContainer.id = "qr-bulk-print";

    printContainer.innerHTML = `
    <style>
      @media print {
        body * {
          visibility: hidden;
        }
        #qr-bulk-print, #qr-bulk-print * {
          visibility: visible;
        }
        #qr-bulk-print {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          padding: 20px;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          font-family: Arial, sans-serif;
        }
        .qr-item {
          text-align: center;
          page-break-inside: avoid;
        }
        svg {
          width: 160px;
          height: 160px;
        }
      }
    </style>
    ${tables
      .map(
        (t) => `
          <div class="qr-item">
            <h4>Table ${t.tableNumber}</h4>
            ${t.qrImage || ""}
          </div>
        `,
      )
      .join("")}
  `;

    document.body.appendChild(printContainer);
    window.print();
    document.body.removeChild(printContainer);
  };

  return {
    tables,
    lastRefreshed,
    fetchTables,
    handleToggleTable,
    createOrUpdateTable,
    handleBulkPrintDirect,
  };
};
